import React from 'react'
import ErrorPage from 'components/ErrorPage'
import withErrorBoundary from 'hoc/withErrorBoundary'
import Loadable from 'react-loadable'
import pRetry from 'p-retry'
import withError from 'hoc/withError'
import { track as trackError } from 'lib/utils/errors'
import { Pane, Spinner, majorScale } from 'evergreen-v4'

const withErrorHandling = withError({
  path: 'error',
  title: "Couldn't load page",
  messaging: 'An error occurred and we were unable to load this page',
  primaryButton: {
    children: 'Reload',
    onClick: () => window.location.reload()
  }
})

const Loading = withErrorHandling(({ pastDelay }) => {
  // Don't render the spinner if the bundle takes less than 200ms to load
  // https://github.com/jamiebuilds/react-loadable#avoiding-flash-of-loading-component
  if (!pastDelay) {
    return null
  }

  return (
    <Pane display="flex">
      <Spinner
        size={majorScale(3)}
        flexBasis={majorScale(3)}
        marginY={majorScale(8)}
        marginX="auto"
      />
    </Pane>
  )
})

const loadable = loader => {
  function retryLoader() {
    return loader().catch(error => {
      // Only retry network errors because webpack will just return an empty module
      // when you try re-importing a module that failed to load due to a javascript error
      if (typeof error.request !== 'string') {
        // Track the javascript error
        trackError(error)
        throw new pRetry.AbortError(error)
      }

      // Don't retry css loading errors because webpack won't retry fetching them
      if (!error.request.endsWith('.js')) {
        throw new pRetry.AbortError(error)
      }

      throw error
    })
  }

  const AsyncComponent = Loadable({
    loader: () => {
      return pRetry(retryLoader, { retries: 3, minTimeout: 300 })
    },
    loading: Loading,
    delay: 200
  })

  return withErrorBoundary({ errorComponent: ErrorPage })(AsyncComponent)
}

export default loadable
